import { useEffect, useState, useMemo, useCallback } from 'react'
import { Box, IconButton, PopperPlacementType, Stack, Tooltip } from '@mui/material'
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material'
import { startOfDay } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import Paragraph from '../../../shared/UI/Paragraph'
import NewBooking from './NewBooking'
import { BookingNewBookingRange, PopoverProps } from './types'
import { TabDetails } from '../../../shared/UI/Tab'
import { RootStore } from '../../../redux/store'
import { combineDateAndTime, doesBookingOverlapWithPeriod, getJson, getPeriodFromBooking } from '../utils/utils'
import { BookingAvailability, FULL_DAY_RANGE, MobileOrientationPopOverOptions, ManagerBookingOptions } from '../consts'
import { SelectOption } from '../../../services/dashboardService'
import {
  setBookingWizardSelectedFeatureState,
  setBookingWizardSelectedAlternativeFeatureState,
} from '../../../redux/reducers/deskBookingWizardReducer'
import { BookingWizardSteps } from '../BlockBooking/enums'
import { BookingZIndexLayers, PopoverTabLabel } from '../Shared/enums'

import PopoverWrapper from './PopoverWrapper'
import BookingPopoverComponent from './BookingPopover'
import { ResponsiveDetectionMobileState } from '../../../types/responsive-detection-mobile'
import { createDateFromTimeString, formatLocalDateYmd } from '../../../utils/date-utils'
import { BookingBookingPayload } from '../../../services/booking/types'
import theme from '../../../theme/theme'
import { BookingPeriods } from './enums'
import { BookingWindow } from '../Shared/BookingWindow'


function DeskPopover({
  svgElement,
  floorplan,
  open,
  featureDetail,
  bookings,
  isOwnBooking,
  dateTimeFrom,
  isBlockBooking,
  isManager,
  availability,
  onClose,
  onSubmit,
  onCancel,
  bookingFor: externalBookingFor,
  selectedEmployee: externalSelectedEmployee,
  onBookingForChange: externalOnBookingForChange,
}: PopoverProps) {
  const dispatch = useDispatch()

  const {
    mobileDetection
  } = useSelector((state: RootStore) => state.responsiveDetection)
  const { employeeDetails } = useSelector((state: RootStore) => state.appSettings)

  const {
    isMobile,
    mobileLandscape,
    mobilePortait
  }:ResponsiveDetectionMobileState = mobileDetection

  const [selectedTab, setSelectedTab] = useState<number>(0)
  const [newBookingSelectedRange, setNewBookingSelectedRange] = useState<BookingNewBookingRange>(FULL_DAY_RANGE)
  const [selectedBookingLengthOption, setSelectedBookingLengthOption] = useState(BookingPeriods.FULL_DAY)
  const [newBookingHasError, setNewBookingHasError] = useState(false)
  
  // Controlled/uncontrolled pattern for bookingFor and selectedEmployee
  const isBookingForControlled = externalBookingFor !== undefined
  const [internalBookingFor, setInternalBookingFor] = useState<number>(ManagerBookingOptions.MYSELF)
  const [internalSelectedEmployee, setInternalSelectedEmployee] = useState<SelectOption>()
  
  const currentBookingFor = isBookingForControlled ? externalBookingFor! : internalBookingFor
  const currentSelectedEmployee = isBookingForControlled ? externalSelectedEmployee : internalSelectedEmployee

  const { floorplanViewingDate, locations, floorplanManuallySelected, showFloorplan, hasBookingRestriction } = useSelector(
    (state: RootStore) => state.deskBooking
  )
  const { currentStep: currentWizardStep } = useSelector(
    (state: RootStore) => state.deskBookingWizard
  )
  const { searchResults, searchParams } = useSelector((state: RootStore) => state.deskBookingSearch)

  const isPastDate = floorplanViewingDate < startOfDay(new Date())
  const singleBookingFloorplanSelected = !isBlockBooking && floorplanManuallySelected
  const locationName = locations.find(f => f.id === featureDetail.locationId)?.name || ''

  const popoverWidth = 300

  useEffect(() => {
    if (!searchResults || !searchParams) {
      return
    }

    setNewBookingSelectedRange({
      from: createDateFromTimeString(searchParams.from),
      to: createDateFromTimeString(searchParams.to),
    })
  }, [searchResults, searchParams])

  useEffect(() => {
    if (!showFloorplan) {
      onClose?.()
    }
  }, [showFloorplan, onClose])

  const additionalInfo = getJson(featureDetail.additionalInfo)

  const mobilePopoverPosition = useCallback(() => {

    if (isMobile) {

      if (mobileLandscape) return MobileOrientationPopOverOptions.MOBILE_LANDSCAPE
      if (mobilePortait) return MobileOrientationPopOverOptions.MOBILE_PORTRAIT
    }

    return MobileOrientationPopOverOptions.NOT_MOBILE

  }, [isMobile, mobileLandscape, mobilePortait])

  const desktopPopoverPosition = () => Number(featureDetail.ports?.[0].x) || 2

  const popoverPosition = isMobile ? mobilePopoverPosition() : desktopPopoverPosition()

  const getPopoverPosition = useCallback((): PopperPlacementType => {
    switch (popoverPosition) {
      case 1:
        return 'top'
      case 2:
        return 'right'
      case 3:
        return 'bottom'
      case 4:
        return 'left'
      default:
        return 'right'
    }
  }, [popoverPosition])

  const cancelBooking = (booking: BookingBookingPayload) => 
    onCancel?.({
      bookingID: booking.id,
      deskName: `${additionalInfo?.rowName ? `${additionalInfo.rowName}-` : ''}${featureDetail.label}`,
      floorPlanName: floorplan,
      byManager: isManager && booking?.employeeId !== employeeDetails.employeeId,
      location: locationName,
      date: dateTimeFrom,
      onCallBack: success => undefined,
    })

  const renderOtherBookings = (otherBookings: BookingBookingPayload[]) => {
    const sortedBookings = otherBookings.sort((a, b) => {
      const aDate = combineDateAndTime(a.fromDate, a.fromTime)
      const bDate = combineDateAndTime(b.fromDate, b.fromTime)
      return aDate.getTime() - bDate.getTime()
    })

    return (
      <Stack direction="column" gap="4px" maxHeight="132px" overflow="auto">
        { sortedBookings.map(booking => (
          <Box
            key={booking.id}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            style={{ 
              backgroundColor: 'rgba(144, 144, 144, 0.05)',
              borderRadius: '8px',
              color: '#909090',
              padding: '8px'
            }}
          >
            <Paragraph
              size="11px"
              sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', marginRight: '4px' }}
            >
              { booking.displayName }
            </Paragraph>
            <Stack display="flex" flexDirection="row" alignItems="center" flexShrink={0} gap="8px">
              <Paragraph
                size="11px"
              >
                <BookingWindow booking={booking} selectedDate={floorplanViewingDate} />
              </Paragraph>
              {isManager && !isPastDate &&
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={() => {
                    cancelBooking(booking)
                  }}
                  aria-label="Cancel Desk"
                >
                  <Tooltip title="Cancel Desk" placement="top">
                    <DeleteOutlineIcon sx={{ color: theme.palette.primary.main }} />
                  </Tooltip>
                </IconButton>
              }
            </Stack>
          </Box>
        ))}
      </Stack>
    )
  }

  const getTabSelectionProps = () => {
    const contentArray: TabDetails[] = []
    if (isBlockBooking) {
      if (bookings && bookings?.length > 0) {
        const reservedBy = bookings.length > 1 ? 'multiple bookings' : bookings[0].displayName
        contentArray.push({
          label: 'Bookings',
          tabContext: (
            <Paragraph
              size="11px"
              padding="8px"
              style={{ 
                backgroundColor: 'rgba(144, 144, 144, 0.05)',
                borderRadius: '8px',
                color: '#909090'
              }}
            >
              {`Reserved by ${reservedBy}`}
            </Paragraph>
          )
        })
      }
      return contentArray
    }

    if (isOwnBooking) {
      const ownBookings = bookings?.filter(
        booking => booking.employeeId === employeeDetails.employeeId
        && doesBookingOverlapWithPeriod(booking, floorplanViewingDate, newBookingSelectedRange.from, newBookingSelectedRange.to)
      )
      if (ownBookings && ownBookings?.length >= 1) {
        const ownBooking = ownBookings[0]
        const bookingPeriod = getPeriodFromBooking(ownBooking)
        const bookingTimePrefix = bookingPeriod === BookingPeriods.CUSTOM_TIME_SLOT ? 'Time: ' : ''
        contentArray.push({
          label: PopoverTabLabel.MY_DESK,
          tabContext: (
            <Paragraph
              style={{ 
                backgroundColor: 'rgba(144, 144, 144, 0.05)',
                borderRadius: '8px',
                color: '#909090'
              }}
              padding="8px"
              size="11px"
            >
              {bookingTimePrefix}
              <BookingWindow booking={ownBooking} selectedDate={floorplanViewingDate} />
            </Paragraph>
          )
        })
      }
    }

    const isDeskBookable = !isPastDate
      && !isOwnBooking
      && (
        availability === BookingAvailability.AVAILABLE
        || (availability === BookingAvailability.PARTIAL_AVAILABILITY && singleBookingFloorplanSelected))

    if (isDeskBookable) {
      // if user ended up on the floor plan through the search flow, do not show the booking length inputs
      if (!singleBookingFloorplanSelected) {
        contentArray.push({
          label: PopoverTabLabel.BOOK,
          tabContext: null
        })
      } else {
        const allRelevantBookings = searchResults?.filter(
          booking => booking.featureId === featureDetail.id
        )
        contentArray.push({
          label: PopoverTabLabel.BOOK,
          tabContext: (
            <Box marginTop="10px">
              <NewBooking
                bookings={allRelevantBookings ?? []}
                selectedDate={floorplanViewingDate}
                selectedBookingLength={selectedBookingLengthOption}
                selectedBookingRange={newBookingSelectedRange}
                onSelectedRange={newBooking => {
                  setNewBookingSelectedRange(newBooking.selectedRange)
                }}
                onBookingLengthChange={newBookingLength => {
                  setSelectedBookingLengthOption(newBookingLength)
                }}
                onErrorStateChange={(hasError) => setNewBookingHasError(hasError)}
                isManager={isManager}
                bookingFor={currentBookingFor}
                selectedEmployee={currentSelectedEmployee}
                onBookingForChange={(bookingForValue, employee) => {
                  if (isBookingForControlled && externalOnBookingForChange) {
                    externalOnBookingForChange(bookingForValue, employee)
                  } else if (!isBookingForControlled) {
                    setInternalBookingFor(bookingForValue)
                    setInternalSelectedEmployee(employee)
                  }
                  // If controlled but no onChange handler, don't update anything
                }}
              />
            </Box>
          )
        })
      }
    }

    const otherBookings = bookings?.filter(booking =>
      booking.employeeId !== employeeDetails.employeeId
      && booking.fromDate <= formatLocalDateYmd(floorplanViewingDate)
    )
    if (otherBookings && otherBookings?.length >= 1) {
        contentArray.push({
          label: PopoverTabLabel.OTHER_BOOKINGS,
          tabContext: renderOtherBookings(otherBookings)
      })
    }
    return contentArray
  }

  const tabSelectionProps = getTabSelectionProps()

  const BookingFeaturePopover = useMemo(() => 
    PopoverWrapper(isMobile)(BookingPopoverComponent), [isMobile])

  const selectedTabLabel = tabSelectionProps[selectedTab]?.label
  const isMyDeskSelected = selectedTabLabel === PopoverTabLabel.MY_DESK

  const isBlockAndIsNotAvailable =
    isBlockBooking
      && (availability === BookingAvailability.NO_AVAILABILITY
          || (availability === BookingAvailability.PARTIAL_AVAILABILITY && currentWizardStep === BookingWizardSteps.STEP_4_ALTERNATIVES))

  const getSubmitLabel = () => {
    if (isPastDate) {
      return 'Disabled'
    }
    if (hasBookingRestriction) {
      return 'Restricted'
    }
    if (isBlockBooking) {
      return isBlockAndIsNotAvailable ? 'Disabled' : 'Select'
    }

    if (selectedTabLabel === PopoverTabLabel.BOOK
      && (availability === BookingAvailability.AVAILABLE
          || (availability === BookingAvailability.PARTIAL_AVAILABILITY))) {
      return 'Book Now'
    }

    if (isMyDeskSelected) {
      return 'Cancel Desk'
    }
    
    return 'Disabled'
  }

  const getSubmitDisabledState = () => {
    if (isPastDate) {
      return true
    }
    if (hasBookingRestriction) {
      return true
    }
    if (isMyDeskSelected) {
      return false
    }
    if (isBlockAndIsNotAvailable || newBookingHasError) {
      return true
    }
    if (!isBlockBooking && availability !== BookingAvailability.AVAILABLE) {
      if (availability === BookingAvailability.PARTIAL_AVAILABILITY) {
        return false
      }
      return !isOwnBooking
    }
    return false
  }

  const validateBlockBooking = () => {
    onClose?.()
    if (isBlockBooking && currentWizardStep === BookingWizardSteps.STEP_4_ALTERNATIVES) {
      return dispatch(setBookingWizardSelectedAlternativeFeatureState(featureDetail.id))
    }
    return dispatch(
      setBookingWizardSelectedFeatureState({
        ...featureDetail,
        hasBookings: availability !== BookingAvailability.AVAILABLE,
      })
    )
  }

  const onButtonClick = () => {
    if (isBlockBooking) {
      return validateBlockBooking()
    }

    if (isMyDeskSelected) {
      const ownBooking = bookings?.find(booking => booking.employeeId === employeeDetails.employeeId)
      if (ownBooking) {
        return cancelBooking(ownBooking)
      }
    }
    
    const employeeId = currentBookingFor === ManagerBookingOptions.EMPLOYEE && currentSelectedEmployee?.value
      ? currentSelectedEmployee.value
      : employeeDetails.employeeId
    
    const employeeName = currentBookingFor === ManagerBookingOptions.EMPLOYEE && currentSelectedEmployee?.displayValue
      ? currentSelectedEmployee.displayValue
      : `${employeeDetails.employeeForename} ${employeeDetails.employeeSurname}`

    onSubmit?.(featureDetail, `${additionalInfo?.rowName ? `${additionalInfo.rowName}-` : ''}${featureDetail.label}`, {
      date: floorplanViewingDate,
      selectedRange: newBookingSelectedRange,
      employeeId,
      employeeName,
      bookingFor: currentBookingFor,
    })
  }

  const {
    BOOKING_FLOORPLAN_VIEW_FEATURE_POPOVER
  } = BookingZIndexLayers

  return (
    <BookingFeaturePopover
      open={open}
      anchorEl={svgElement}
      placement={getPopoverPosition()}
      style={{
        zIndex: BOOKING_FLOORPLAN_VIEW_FEATURE_POPOVER,
        width: '296px',
      }}
      popoverProps={{
        isMobile,
        popoverPosition,
        popoverWidth,
        isOwnBooking,
        deskLabel: `Desk ${additionalInfo?.desc?.deskDesc ? additionalInfo.desc.deskDesc : ''}${
          featureDetail.label
        }`,
        availability,
        date: floorplanViewingDate,
        showDate: !isBlockBooking,
        getSubmitLabel,
        getSubmitDisabledState,
        tabSelectionProps,
        onButtonClick,
        setSelectedTab,
        onClickCallback: () => {
          onClose?.()
        }
      }}
    />
  )

}

export default DeskPopover
